import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
	Search,
	Clock,
	TrendingUp,
	X,
	ShoppingBag,
	Star,
	Tag,
	AlertCircle,
	Loader2,
	Package,
} from 'lucide-react'
import { Entities } from '@/entities/api/entities'
import {
	FilteredProductsResponse,
	FilteredProduct,
} from '@/entities/api/products/product-filters'
import { ProductEntity } from '@/entities/api/products/product'
import { quickSearch } from '@/services/api/products/products-filter'
import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { API_URL } from '@/services/api/keys'

interface SearchDropdownProps {
	isOpen: boolean
	onClose: () => void
	searchQuery: string
	onSearchChange: (query: string) => void
}

interface SearchError {
	message: string
	type: 'network' | 'server' | 'unknown'
}

interface NavigableItem {
	type: 'product' | 'viewAll' | 'recent' | 'popular'
	index: number
	data?: any
}

const SEARCH_CONFIG = {
	MIN_CHARS: 2,
	DEBOUNCE_DELAY: 300,
	MAX_RESULTS: 8,
	MAX_RECENT_SEARCHES: 5,
	REQUEST_TIMEOUT: 5000,
} as const

const SearchDropdown: React.FC<SearchDropdownProps> = ({
	isOpen,
	onClose,
	searchQuery,
	onSearchChange,
}) => {
	const navigate = useNavigate()
	const [searchResults, setSearchResults] =
		useState<FilteredProductsResponse | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<SearchError | null>(null)
	const [recentSearches, setRecentSearches] = useState<string[]>([])
	const [selectedIndex, setSelectedIndex] = useState(-1)

	const categories = useSelector(
		(state: RootState) => state.category.categories
	)
	const [popularSearches, setPopularSearches] = useState<string[]>([])

	const searchTimeoutRef = useRef<NodeJS.Timeout>()
	const dropdownRef = useRef<HTMLDivElement>(null)
	const abortControllerRef = useRef<AbortController | null>(null)
	const mobileInputRef = useRef<HTMLInputElement>(null)
	const selectedItemRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)
	const selectedButtonRef = useRef<HTMLButtonElement>(null)
	const lastSearchRef = useRef<string>('')

	const memoizedPopularSearches = useMemo(() => {
		if (!categories?.length) {
			return [
				'Proteínas',
				'Creatina',
				'Pre-entreno',
				'BCAA',
				'Whey Protein',
				'Camisetas',
				'Shorts',
				'Zapatillas',
				'Guantes',
			]
		}

		return categories
			.slice(1, 10)
			.map((c) => c.name)
			.filter(Boolean)
	}, [categories])

	useEffect(() => {
		setPopularSearches(memoizedPopularSearches)
	}, [memoizedPopularSearches])

	useEffect(() => {
		try {
			const stored = localStorage.getItem('recentSearches')
			if (stored) {
				const parsed = JSON.parse(stored)
				if (Array.isArray(parsed)) {
					setRecentSearches(parsed.slice(0, SEARCH_CONFIG.MAX_RECENT_SEARCHES))
				}
			}
		} catch (err) {
			console.warn('Error loading recent searches:', err)
			setRecentSearches([])
		}
	}, [])

	const performSearch = useCallback(async (query: string) => {
		const trimmedQuery = query.trim()

		if (!trimmedQuery || trimmedQuery.length < SEARCH_CONFIG.MIN_CHARS) {
			setSearchResults(null)
			setError(null)
			setSelectedIndex(-1)
			setIsLoading(false)
			return
		}

		if (trimmedQuery === lastSearchRef.current) {
			return
		}

		lastSearchRef.current = trimmedQuery

		if (abortControllerRef.current) {
			abortControllerRef.current.abort()
		}

		abortControllerRef.current = new AbortController()
		const timeoutId = setTimeout(() => {
			abortControllerRef.current?.abort()
		}, SEARCH_CONFIG.REQUEST_TIMEOUT)

		setIsLoading(true)
		setError(null)
		setSelectedIndex(-1)

		try {
			const response = await quickSearch(
				trimmedQuery,
				1,
				SEARCH_CONFIG.MAX_RESULTS
			)

			clearTimeout(timeoutId)

			if (!response || !response.data) {
				throw new Error('Invalid response format')
			}

			let searchData: FilteredProductsResponse | null = null

			if (response.data && Array.isArray(response.data.products)) {
				searchData = response.data as FilteredProductsResponse
			} else if (response.data && Array.isArray(response.data)) {
				searchData = {
					products: response.data,
					totalCount: response.data.length,
					currentPage: 1,
					totalPages: Math.ceil(
						response.data.length / SEARCH_CONFIG.MAX_RESULTS
					),
					limit: SEARCH_CONFIG.MAX_RESULTS,
					appliedFilters: { searchTerm: trimmedQuery },
				}
			}

			if (searchData && Array.isArray(searchData.products)) {
				setSearchResults(searchData)
			} else {
				setSearchResults({
					products: [],
					totalCount: 0,
					currentPage: 1,
					totalPages: 0,
					limit: SEARCH_CONFIG.MAX_RESULTS,
					appliedFilters: { searchTerm: trimmedQuery },
				})
			}
		} catch (err: any) {
			clearTimeout(timeoutId)

			if (err.name === 'AbortError') {
				return
			}

			let errorInfo: SearchError = {
				message: 'Error al buscar productos',
				type: 'unknown',
			}

			if (err.code === 'NETWORK_ERROR' || !navigator.onLine) {
				errorInfo = {
					message: 'Sin conexión a internet. Verifica tu conexión.',
					type: 'network',
				}
			} else if (err.response?.status >= 500) {
				errorInfo = {
					message: 'El servidor está temporalmente no disponible.',
					type: 'server',
				}
			} else if (err.response?.status === 429) {
				errorInfo = {
					message: 'Demasiadas búsquedas. Espera un momento.',
					type: 'server',
				}
			} else if (err.message && err.message !== 'Invalid response format') {
				errorInfo.message = err.message
			}

			setError(errorInfo)
			setSearchResults(null)
		} finally {
			setIsLoading(false)
		}
	}, [])

	useEffect(() => {
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}

		setSelectedIndex(-1)

		const trimmedQuery = searchQuery.trim()

		if (trimmedQuery.length < SEARCH_CONFIG.MIN_CHARS) {
			setSearchResults(null)
			setError(null)
			setIsLoading(false)
			lastSearchRef.current = ''
			return
		}

		if (trimmedQuery.length >= SEARCH_CONFIG.MIN_CHARS) {
			setIsLoading(true)
		}

		searchTimeoutRef.current = setTimeout(() => {
			performSearch(trimmedQuery)
		}, SEARCH_CONFIG.DEBOUNCE_DELAY)

		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [searchQuery, performSearch])

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}
		}
	}, [])

	const addToRecentSearches = useCallback((query: string) => {
		const trimmedQuery = query.trim()
		if (!trimmedQuery || trimmedQuery.length < SEARCH_CONFIG.MIN_CHARS) return

		try {
			setRecentSearches((prevSearches) => {
				const updated = [
					trimmedQuery,
					...prevSearches.filter((s) => s !== trimmedQuery),
				].slice(0, SEARCH_CONFIG.MAX_RECENT_SEARCHES)

				localStorage.setItem('recentSearches', JSON.stringify(updated))
				return updated
			})
		} catch (err) {
			console.warn('Error saving recent search:', err)
		}
	}, [])

	const handleSearchSelect = useCallback(
		(query: string) => {
			onSearchChange(query)
			addToRecentSearches(query)
		},
		[onSearchChange, addToRecentSearches]
	)

	const getNavigableItems = useCallback((): NavigableItem[] => {
		const items: NavigableItem[] = []

		if (searchResults && searchResults.products.length > 0) {
			searchResults.products.forEach((product, index) => {
				items.push({ type: 'product', index, data: product })
			})
			if (searchResults.totalCount > SEARCH_CONFIG.MAX_RESULTS) {
				items.push({ type: 'viewAll', index: -1 })
			}
		} else if (
			(!searchQuery.trim() ||
				searchQuery.trim().length < SEARCH_CONFIG.MIN_CHARS) &&
			!isLoading &&
			!error
		) {
			recentSearches.forEach((search, index) => {
				items.push({ type: 'recent', index, data: search })
			})
			popularSearches.forEach((search, index) => {
				items.push({ type: 'popular', index, data: search })
			})
		}

		return items
	}, [
		searchResults,
		searchQuery,
		isLoading,
		error,
		recentSearches,
		popularSearches,
	])

	const scrollToSelectedItem = useCallback(() => {
		const elementToScroll = selectedItemRef.current || selectedButtonRef.current
		if (elementToScroll && dropdownRef.current) {
			const container = dropdownRef.current.querySelector(
				'.flex-1.overflow-y-auto'
			) as HTMLElement
			if (container) {
				const itemRect = elementToScroll.getBoundingClientRect()
				const containerRect = container.getBoundingClientRect()

				if (itemRect.bottom > containerRect.bottom) {
					container.scrollTop += itemRect.bottom - containerRect.bottom + 20
				} else if (itemRect.top < containerRect.top) {
					container.scrollTop -= containerRect.top - itemRect.top + 20
				}
			}
		}
	}, [])

	useEffect(() => {
		scrollToSelectedItem()
	}, [selectedIndex, scrollToSelectedItem])

	const handleEnterNavigation = useCallback(() => {
		const trimmedQuery = searchQuery.trim()
		if (!trimmedQuery) return

		const items = getNavigableItems()

		if (selectedIndex >= 0 && selectedIndex < items.length) {
			const selectedItem = items[selectedIndex]

			switch (selectedItem.type) {
				case 'product':
					const product = selectedItem.data as FilteredProduct
					const productData = getProductData(product)
					addToRecentSearches(trimmedQuery)
					navigate(
						`/Producto/${productData?.name.replace(/ /g, '-')}/${
							productData.id
						}`
					)
					break

				case 'recent':
				case 'popular':
					const query = selectedItem.data as string
					handleSearchSelect(query)
					break

				case 'viewAll':
					addToRecentSearches(trimmedQuery)
					navigate(`/buscar?q=${encodeURIComponent(trimmedQuery)}`)
					break
			}
		} else {
			addToRecentSearches(trimmedQuery)
			navigate(`/Buscar?q=${encodeURIComponent(trimmedQuery)}`)
		}
	}, [
		selectedIndex,
		searchQuery,
		getNavigableItems,
		addToRecentSearches,
		navigate,
		handleSearchSelect,
	])

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!isOpen) return

			const items = getNavigableItems()

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault()
					setSelectedIndex((prev) => {
						if (items.length === 0) return -1
						const next = prev < items.length - 1 ? prev + 1 : 0
						return next
					})
					break

				case 'ArrowUp':
					e.preventDefault()
					setSelectedIndex((prev) => {
						if (items.length === 0) return -1
						const next = prev > 0 ? prev - 1 : items.length - 1
						return next
					})
					break

				case 'Enter':
					e.preventDefault()
					handleEnterNavigation()
					break

				case 'Tab':
					e.preventDefault()
					setSelectedIndex((prev) => {
						if (items.length === 0) return -1
						const next = prev < items.length - 1 ? prev + 1 : 0
						return next
					})
					break
			}
		},
		[isOpen, getNavigableItems, handleEnterNavigation]
	)

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [handleKeyDown])

	const handleInputEnter = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && searchQuery.trim()) {
				e.preventDefault()
				handleEnterNavigation()
			}
		},
		[searchQuery, handleEnterNavigation]
	)

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value
			onSearchChange(newValue)
		},
		[onSearchChange]
	)

	const handleItemHover = useCallback((index: number) => {
		setSelectedIndex(index)
	}, [])

	const handleItemLeave = useCallback(() => {}, [])

	const formatPrice = useCallback((price: number) => {
		if (typeof price !== 'number' || isNaN(price)) return 'Precio no disponible'

		return new Intl.NumberFormat('es-CO', {
			style: 'currency',
			currency: 'COP',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(price)
	}, [])

	const getEntityIcon = useCallback((type: Entities) => {
		const iconMap = {
			[Entities.SUPPLEMENTS]: '💊',
			[Entities.CLOTHES]: '👕',
			[Entities.ACCESSORIES]: '🏋️',
			[Entities.PLANS_PERSONALIZED]: '🎯',
		}
		return iconMap[type] || '📦'
	}, [])

	const getProductData = useCallback((item: FilteredProduct) => {
		const product = item?.product || ({} as ProductEntity)
		const priceInfo = item?.priceInfo || {
			finalPrice: 0,
			basePrice: 0,
			discountPercentage: 0,
		}
		const statistics = item?.statistics || { averageRating: 0 }

		return {
			id: product._id || `product-${Math.random()}`,
			name: product.name || 'Producto sin nombre',
			brand: product.brand || 'Marca no disponible',
			categoryName: product.category?.name || 'Categoría no disponible',
			type: product.type || Entities.SUPPLEMENTS,
			attributes: product.attributes,
			images: product.images || [],
			discount: product.discount || 0,
			finalPrice: priceInfo.finalPrice || 0,
			basePrice: priceInfo.basePrice || 0,
			discountPercentage: priceInfo.discountPercentage || 0,
			averageRating: statistics.averageRating || 0,
		}
	}, [])

	const getCurrentItemIndex = useCallback(
		(type: string, index: number) => {
			const items = getNavigableItems()

			for (let i = 0; i < items.length; i++) {
				if (items[i].type === type && items[i].index === index) {
					return i
				}
			}

			return -1
		},
		[getNavigableItems]
	)

	if (!isOpen) return null

	const trimmedQuery = searchQuery.trim()
	const isQueryTooShort =
		trimmedQuery.length > 0 && trimmedQuery.length < SEARCH_CONFIG.MIN_CHARS

	return (
		<div className='fixed inset-0 z-[999999] lg:absolute lg:inset-auto lg:top-full lg:right-0 lg:w-[min(650px,90vw)] xl:w-[min(750px,85vw)] lg:mt-2'>
			<div
				ref={dropdownRef}
				className='relative lg:relative bg-white rounded-none lg:rounded-3xl shadow-2xl border-0 lg:border lg:border-gray-200/80 overflow-hidden max-h-screen lg:max-h-[85vh] flex flex-col backdrop-blur-lg lg:backdrop-blur-none'
				style={{
					background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
					boxShadow:
						'0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
				}}
			>
				<button
					onClick={onClose}
					className='absolute top-4 right-4 z-50 p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 hover:scale-105 group'
					aria-label='Cerrar búsqueda'
				>
					<X className='w-5 h-5 text-gray-500 group-hover:text-red-500 transition-colors duration-200' />
				</button>

				<div className='lg:hidden flex items-center justify-between p-6 pr-16 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white'>
					<div className='flex items-center space-x-3'>
						<div className='w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg'>
							<Search className='w-4 h-4 text-white' />
						</div>
						<h3 className='text-xl font-bold text-gray-900 tracking-tight'>
							Buscar productos
						</h3>
					</div>
				</div>

				<div className='flex-1 overflow-y-auto'>
					<div className='lg:hidden px-4 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white'>
						<div className='relative group w-full max-w-full'>
							<Search className='absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-red-500 transition-colors duration-200' />

							<input
								ref={mobileInputRef}
								type='text'
								value={searchQuery}
								onChange={handleInputChange}
								onKeyDown={handleInputEnter}
								placeholder={`Buscar productos... (mín. ${SEARCH_CONFIG.MIN_CHARS} caracteres)`}
								className='w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 
                 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:bg-white 
                 text-gray-900 placeholder-gray-500 shadow-sm transition-all duration-200'
								autoFocus
							/>

							{isQueryTooShort && (
								<div className='absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2'>
									<span className='text-[10px] sm:text-xs text-gray-400 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded'>
										{trimmedQuery.length}/{SEARCH_CONFIG.MIN_CHARS}
									</span>
								</div>
							)}
						</div>
					</div>

					{isLoading && (
						<div className='p-8 text-center'>
							<div className='relative mx-auto mb-4 w-12 h-12'>
								<div className='absolute inset-0 rounded-full border-4 border-red-100'></div>
								<div className='absolute inset-0 rounded-full border-4 border-transparent border-t-red-500 animate-spin'></div>
								<div className='absolute inset-2 rounded-full border-2 border-transparent border-t-red-400 animate-spin animate-reverse'></div>
							</div>
							<p className='text-gray-600 text-sm font-medium'>
								Buscando productos increíbles...
							</p>
							<p className='text-gray-400 text-xs mt-1'>
								Búsqueda automática activada
							</p>
						</div>
					)}

					{error && !isLoading && (
						<div className='p-8 text-center'>
							<div className='w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4'>
								<AlertCircle className='w-8 h-8 text-red-500' />
							</div>
							<h4 className='text-lg font-semibold text-gray-900 mb-2'>
								Oops, algo salió mal
							</h4>
							<p className='text-sm text-gray-600 mb-4'>{error.message}</p>
							<button
								onClick={() => performSearch(searchQuery)}
								className='px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105'
							>
								Reintentar búsqueda
							</button>
						</div>
					)}

					{searchResults &&
						searchResults.products.length > 0 &&
						!isLoading &&
						!error && (
							<div className='p-6 lg:p-8'>
								<div className='flex items-center justify-between mb-6'>
									<div className='flex items-center space-x-3'>
										<div className='w-8 h-8 bg-gradient-to-br from-green-400 to-green-500 rounded-xl flex items-center justify-center'>
											<Package className='w-4 h-4 text-white' />
										</div>
										<h4 className='text-lg font-bold text-gray-900 tracking-tight'>
											Resultados ({searchResults.totalCount})
										</h4>
									</div>
									{searchResults.totalCount > SEARCH_CONFIG.MAX_RESULTS && (
										<Link
											ref={
												selectedIndex === searchResults.products.length
													? (selectedItemRef as React.RefObject<HTMLAnchorElement>)
													: null
											}
											to={`/buscar?q=${encodeURIComponent(searchQuery)}`}
											className={`px-4 py-2 text-sm font-semibold transition-all duration-200 flex items-center space-x-2 border rounded-xl shadow-sm transform ${
												selectedIndex === searchResults.products.length
													? 'text-white bg-gradient-to-r from-red-500 to-red-600 border-transparent shadow-lg scale-105'
													: 'text-red-600 hover:text-white hover:bg-gradient-to-r hover:from-red-500 hover:to-red-600 border-red-200 hover:border-transparent hover:shadow-lg hover:scale-105'
											}`}
											onClick={() => {
												addToRecentSearches(searchQuery)
											}}
											onMouseEnter={() =>
												handleItemHover(searchResults.products.length)
											}
											onMouseLeave={handleItemLeave}
										>
											<span>Ver todos</span>
											<TrendingUp className='w-4 h-4' />
										</Link>
									)}
								</div>

								<div className='space-y-3'>
									{searchResults.products.map((item, index) => {
										const productData = getProductData(item)
										const isSelected = selectedIndex === index

										return (
											<Link
												key={productData.id}
												ref={
													isSelected
														? (selectedItemRef as React.RefObject<HTMLAnchorElement>)
														: null
												}
												to={`/Producto/${productData?.name.replace(
													/ /g,
													'-'
												)}/${productData.id}`}
												className={`flex items-center p-4 rounded-2xl transition-all duration-300 group border ${
													isSelected
														? 'bg-gradient-to-r from-red-50/70 to-red-50/40 border-red-200 shadow-lg shadow-red-500/20'
														: 'border-transparent hover:bg-gradient-to-r hover:from-red-50/50 hover:to-red-50/30 hover:border-red-100 hover:shadow-lg hover:shadow-red-500/10'
												}`}
												onClick={() => {
													addToRecentSearches(searchQuery)
												}}
												onMouseEnter={() => handleItemHover(index)}
												onMouseLeave={handleItemLeave}
											>
												<div className='w-16 h-16 lg:w-18 lg:h-18 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0 overflow-hidden shadow-sm group-hover:shadow-md transition-all duration-300'>
													{productData.images?.length > 0 &&
													productData.images[0]?.url ? (
														<img
															src={`${API_URL}/${productData.images[0].url}`}
															alt={productData.name}
															className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-110'
															onError={(e) => {
																e.currentTarget.style.display = 'none'
																e.currentTarget.nextElementSibling?.classList.remove(
																	'hidden'
																)
															}}
														/>
													) : productData.attributes?.length > 0 &&
													  productData.attributes[0]?.image?.url ? (
														<img
															src={`${API_URL}/${productData.attributes[0].image.url}`}
															alt={`${productData.name} - ${productData.attributes[0].value}`}
															className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-110'
															onError={(e) => {
																e.currentTarget.style.display = 'none'
																e.currentTarget.nextElementSibling?.classList.remove(
																	'hidden'
																)
															}}
														/>
													) : null}

													<span
														className={`text-xl ${
															productData.images?.length > 0 ||
															productData.attributes?.[0]?.image
																? 'hidden'
																: ''
														}`}
													>
														{getEntityIcon(productData.type)}
													</span>
												</div>

												<div className='flex-1 min-w-0'>
													<div className='flex items-start justify-between'>
														<div className='flex-1 min-w-0 mr-3'>
															<h5
																className={`text-base font-bold truncate transition-colors duration-200 leading-tight ${
																	isSelected
																		? 'text-red-600'
																		: 'text-gray-900 group-hover:text-red-600'
																}`}
															>
																{productData.name}
															</h5>
															<p className='text-sm text-gray-500 mt-1 truncate font-medium'>
																{productData.brand} • {productData.categoryName}
															</p>

															{productData.averageRating > 0 && (
																<div className='flex items-center mt-2'>
																	<div className='flex items-center bg-yellow-50 px-2 py-1 rounded-lg'>
																		<Star className='w-3.5 h-3.5 text-yellow-500 fill-current' />
																		<span className='text-sm text-yellow-700 ml-1.5 font-semibold'>
																			{productData.averageRating.toFixed(1)}
																		</span>
																	</div>
																</div>
															)}
														</div>

														<div className='text-right flex-shrink-0'>
															{productData.discount > 0 &&
															productData.discountPercentage > 0 ? (
																<div className='space-y-1'>
																	<div className='flex items-center justify-end'>
																		<div className='bg-red-500 text-white px-2 py-1 rounded-lg flex items-center space-x-1'>
																			<Tag className='w-3 h-3' />
																			<span className='text-xs font-bold'>
																				-{productData.discountPercentage}%
																			</span>
																		</div>
																	</div>
																	<div className='text-sm text-gray-400 line-through font-medium'>
																		{formatPrice(productData.basePrice)}
																	</div>
																	<div className='text-lg font-bold text-red-600'>
																		{formatPrice(productData.finalPrice)}
																	</div>
																</div>
															) : (
																<div className='text-lg font-bold text-gray-900'>
																	{formatPrice(productData.finalPrice)}
																</div>
															)}
														</div>
													</div>
												</div>
											</Link>
										)
									})}
								</div>
							</div>
						)}

					{searchResults &&
						searchResults.products.length === 0 &&
						!isLoading &&
						!error && (
							<div className='p-8 text-center'>
								<div className='w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-4'>
									<ShoppingBag className='w-10 h-10 text-gray-400' />
								</div>
								<h4 className='text-xl font-bold text-gray-900 mb-2'>
									No encontramos productos
								</h4>
								<p className='text-gray-600 mb-6 max-w-sm mx-auto'>
									Intenta con otros términos de búsqueda o explora nuestras
									categorías populares
								</p>
								<div className='flex flex-wrap gap-2 justify-center'>
									{popularSearches.slice(0, 4).map((search, index) => (
										<button
											key={index}
											onClick={() => handleSearchSelect(search)}
											className='px-4 py-2 bg-gray-100 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all duration-200 text-sm font-medium'
										>
											{search}
										</button>
									))}
								</div>
							</div>
						)}

					{(!trimmedQuery || isQueryTooShort) && !isLoading && !error && (
						<div className='p-6 lg:p-8 space-y-8'>
							{isQueryTooShort && (
								<div className='text-center py-4'>
									<div className='w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4'>
										<Search className='w-8 h-8 text-white' />
									</div>
									<h4 className='text-lg font-semibold text-gray-900 mb-2'>
										Sigue escribiendo...
									</h4>
									<p className='text-sm text-gray-600 mb-3'>
										Escribe al menos {SEARCH_CONFIG.MIN_CHARS} caracteres para
										buscar productos automáticamente
									</p>
									<div className='flex items-center justify-center space-x-2'>
										<span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700'>
											{trimmedQuery.length}/{SEARCH_CONFIG.MIN_CHARS} caracteres
										</span>
										<span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700'>
											Búsqueda automática
										</span>
									</div>
								</div>
							)}

							{recentSearches.length > 0 && (
								<div>
									<h4 className='text-base font-bold text-gray-900 mb-4 flex items-center tracking-tight'>
										<div className='w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mr-3'>
											<Clock className='w-4 h-4 text-white' />
										</div>
										Búsquedas recientes
									</h4>
									<div className='space-y-2'>
										{recentSearches.map((search, index) => {
											const itemIndex = getCurrentItemIndex('recent', index)
											const isSelected = selectedIndex === itemIndex

											return (
												<button
													key={index}
													ref={
														isSelected
															? (selectedItemRef as React.RefObject<HTMLButtonElement>)
															: null
													}
													onClick={() => handleSearchSelect(search)}
													onMouseEnter={() => handleItemHover(itemIndex)}
													onMouseLeave={handleItemLeave}
													className={`flex items-center w-full p-3 rounded-xl transition-all duration-200 text-left group border ${
														isSelected
															? 'bg-gradient-to-r from-blue-50 to-blue-50/50 border-blue-200'
															: 'border-transparent hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-50/50 hover:border-gray-200'
													}`}
												>
													<Clock
														className={`w-4 h-4 mr-3 flex-shrink-0 transition-colors duration-200 ${
															isSelected
																? 'text-blue-500'
																: 'text-gray-400 group-hover:text-blue-500'
														}`}
													/>
													<span
														className={`text-sm font-medium transition-colors duration-200 ${
															isSelected
																? 'text-blue-700'
																: 'text-gray-700 group-hover:text-gray-900'
														}`}
													>
														{search}
													</span>
												</button>
											)
										})}
									</div>
								</div>
							)}

							<div>
								<h4 className='text-base font-bold text-gray-900 mb-4 flex items-center tracking-tight'>
									<div className='w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mr-3'>
										<TrendingUp className='w-4 h-4 text-white' />
									</div>
									Búsquedas populares
								</h4>
								<div className='grid grid-cols-2 gap-3'>
									{popularSearches.map((search, index) => {
										const itemIndex = getCurrentItemIndex('popular', index)
										const isSelected = selectedIndex === itemIndex

										return (
											<button
												key={index}
												ref={isSelected ? selectedButtonRef : null}
												onClick={() => handleSearchSelect(search)}
												onMouseEnter={() => handleItemHover(itemIndex)}
												onMouseLeave={handleItemLeave}
												className={`p-4 rounded-2xl transition-all duration-300 text-left group border shadow-sm transform ${
													isSelected
														? 'bg-gradient-to-br from-red-50 to-red-100 text-red-700 border-red-200 shadow-md scale-105'
														: 'bg-gradient-to-br from-gray-50 to-gray-100 hover:from-red-50 hover:to-red-100 hover:text-red-700 border-gray-200/50 hover:border-red-200 hover:shadow-md hover:scale-105'
												}`}
											>
												<span className='text-sm font-semibold transition-colors duration-200'>
													{search}
												</span>
											</button>
										)
									})}
								</div>
							</div>
						</div>
					)}
				</div>

				<div className='border-t border-gray-200 p-4 bg-gradient-to-r from-gray-50/80 to-white backdrop-blur-sm'>
					<div className='flex items-center justify-between text-xs text-gray-500'>
						<div className='flex items-center space-x-3'>
							<span className='font-medium'>
								{isQueryTooShort && trimmedQuery.length > 0
									? `Escribe ${
											SEARCH_CONFIG.MIN_CHARS - trimmedQuery.length
									  } caracteres más`
									: trimmedQuery.length >= SEARCH_CONFIG.MIN_CHARS
									? 'Búsqueda automática activa'
									: 'Presiona Enter para buscar'}
							</span>
							{trimmedQuery.length >= SEARCH_CONFIG.MIN_CHARS && (
								<span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700'>
									Auto-búsqueda
								</span>
							)}
							{isLoading && (
								<span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700'>
									<Loader2 className='w-3 h-3 mr-1 animate-spin' />
									Buscando...
								</span>
							)}
						</div>
						<div className='flex items-center space-x-4'>
							<span className='flex items-center font-medium'>
								<Search className='w-3 h-3 mr-1.5' />
								Buscar
							</span>
							<span className='hidden sm:flex items-center font-medium'>
								↑↓ Navegar
							</span>
							<span className='hidden md:flex items-center font-medium'>
								Tab/Enter Seleccionar
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default SearchDropdown
